import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { AllocateInterviewRequest, AllocationService } from './allocation.service';
import { Allocation, Interviewer } from '../../interfaces/allocation/allocation.interface';
import { environment } from '../../../../../environments/environment.dev';
import { AllocationSearchParam } from '../../interfaces/allocation/allocation-search-param.interface';
import { convertToHttpParams } from '../../../../core/interfaces/common-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class AllocationApiService extends AllocationService {
    private http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiBaseUrl}v1/interview-bookings`;

    constructor() {
        super();
    }

    override findAll(searchParam: AllocationSearchParam): Observable<PageResponse<Allocation>> {
        return this.wrapRequest(
            this.http.get<any>(this.baseUrl, {
                params: convertToHttpParams(searchParam),
                headers: this.headers
            }).pipe(
                map(res => {
                    const data = res.response || res;
                    return data as PageResponse<Allocation>;
                })
            )
        );
    }

    override find(id: string): Observable<Allocation> {
        return this.wrapRequest(
            this.http.get<any>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
                map(res => res.response || res)
            )
        );
    }

    override create(data: Partial<Allocation>): Observable<Allocation> {
        return this.wrapRequest(
            this.http.post<any>(this.baseUrl, data, { headers: this.headers }).pipe(
                map(res => res.response || res)
            )
        );
    }

    override update(id: string, data: Partial<Allocation>): Observable<Allocation> {
        return this.wrapRequest(
            this.http.put<any>(`${this.baseUrl}/${id}`, data, { headers: this.headers }).pipe(
                map(res => res.response || res)
            )
        );
    }

    override delete(id: string): Observable<void> {
        return this.wrapRequest(
            this.http.delete<void>(`${this.baseUrl}/${id}`, { headers: this.headers })
        );
    }

    override getInterviewers(id?: string): Observable<Interviewer[]> {
        const interviewerUrl = `${environment.apiBaseUrl}v1/interviewer`;
        let params = {};
      
        
        return this.wrapRequest(
            this.http.get<any>(interviewerUrl, { 
                params,
                headers: this.headers 
            }).pipe(
                map(res => {
                    const data = res.response || res;
                    const content = data.content || data || [];
                    return content.map((s: any) => ({
                        id: s.id,
                        uniqueId: s.interviewerUniqueId || s.uniqueId,
                        name: s.interviewerName || s.fullName || s.name,
                    }));
                })
            )
        );
    }

    override allocate(assessmentUniqueId: string, payload: AllocateInterviewRequest[]): Observable<void> {
        const url = `${this.baseUrl}/allocate/${assessmentUniqueId}`;
        return this.wrapRequest(
            this.http.post<void>(url, payload, { headers: this.headers })
        );
    }

    override approve(data: {
        bookingUniqueId: string;
        interviewerUniqueId: string;
        fromTime: string;
        toTime: string;
        date: string;
    }): Observable<void> {
        const url = `${environment.apiBaseUrl}v1/interview-bookings/approve`;
        return this.wrapRequest(
            this.http.post<void>(url, data, { headers: this.headers })
        );
    }
}
